import { omit, pick } from 'lodash-es';
import type { CSSProperties } from 'react';

import { isObject } from '@infinex-private/ts-utils/object';

import { cn } from './cn';
import { useResponsiveProp, useResponsiveRange } from './responsive';
import type {
  BaseStyleProps,
  BleedProps,
  BoxStyleProps,
  DimensionContainer,
  DimensionFraction,
  DimensionProps,
  MarginProps,
  PaddingProps,
  Responsive,
  SelfLayoutProps,
  UnsafeStyleProps,
} from './types';
import { DIMENSION_CONTAINER } from './types';

export type StyleProps = BaseStyleProps & BoxStyleProps & UnsafeStyleProps;

export function useStyleProps<Props extends StyleProps>(props: Props) {
  const responsiveProp = useResponsiveProp();
  const responsiveRange = useResponsiveRange();
  function getValue<T>(prop: Responsive<T>) {
    return isObject(prop) ? responsiveProp(prop) : prop;
  }
  const { vars, space } = useCssVars(props, getValue);

  const className = cn(
    {
      'hidden!': props.isHidden ? responsiveRange(props.isHidden) : undefined,
    },
    {
      'mb-(--view-margin-bottom)':
        space.bleedBottom || space.marginBottom != null,
      'ml-(--view-margin-left)': space.bleedLeft || space.marginLeft != null,
      'mr-(--view-margin-right)': space.bleedRight || space.marginRight != null,
      'mt-(--view-margin-top)': space.bleedTop || space.marginTop != null,
    },
    {
      'pb-(--view-padding-bottom)': space.paddingBottom != null,
      'pl-(--view-padding-left)': space.paddingLeft != null,
      'pr-(--view-padding-right)': space.paddingRight != null,
      'pt-(--view-padding-top)': space.paddingTop != null,
    },
    {
      'aspect-(--view-aspect-ratio)': props.aspectRatio != null,
      'w-(--view-width)': props.width != null,
      'h-(--view-height)': props.height != null,
      'min-w-(--view-min-width)': props.minWidth != null,
      'max-w-(--view-max-width)': props.maxWidth != null,
      'min-h-(--view-min-height)': props.minHeight != null,
      'max-h-(--view-max-height)': props.maxHeight != null,
    },
    {
      'col-span-(--view-col-span)': props.gridColSpan != null,
      'col-start-(--view-col-start)': props.gridColStart != null,
      'col-end-(--view-col-end)': props.gridColEnd != null,
      'row-span-(--view-row-span)': props.gridRowSpan != null,
      'row-start-(--view-row-start)': props.gridRowStart != null,
      'row-end-(--view-row-end)': props.gridRowEnd != null,

      'basis-(--view-flex-basis)': props.flexBasis != null,
      'flex-1': getValue(props.flex) === true,
      'flex-none': getValue(props.flex) === 'none',
      'flex-initial': getValue(props.flex) === 'initial',
      'flex-auto': getValue(props.flex) === 'auto',
      grow: getValue(props.flexGrow) === true,
      'grow-0': getValue(props.flexGrow) === false,
      shrink: getValue(props.flexShrink) === true,
      'shrink-0': getValue(props.flexShrink) === false,

      'justify-self-start': getValue(props.justifySelf) === 'start',
      'justify-self-center': getValue(props.justifySelf) === 'center',
      'justify-self-end': getValue(props.justifySelf) === 'end',

      'self-start': getValue(props.alignSelf) === 'start',
      'self-center': getValue(props.alignSelf) === 'center',
      'self-end': getValue(props.alignSelf) === 'end',
      'self-stretch': getValue(props.alignSelf) === 'stretch',
      'self-baseline': getValue(props.alignSelf) === 'baseline',

      'order-first': getValue(props.order) === 'first',
      'order-last': getValue(props.order) === 'last',
    },
    props.className
  );
  const style: CSSProperties = {
    ...props.style,
    ...vars,
  };

  return { className, style };
}

function useCssVars(
  props: StyleProps,
  getValue: <T>(prop: Responsive<T>) => T | undefined
) {
  function resolveDimension<T>(prop: Responsive<T>) {
    const value = getValue(prop);
    if (isDimensionFraction(value)) return `calc(${value} * 100%)`;
    if (isDimensionContainer(value)) return DIMENSION_CONTAINER[value];
    return value;
  }

  const bleedLeft = resolveBleedLeft(props);
  const bleedRight = resolveBleedRight(props);
  const bleedTop = resolveBleedTop(props);
  const bleedBottom = resolveBleedBottom(props);
  const marginLeft = resolveMarginLeft(props);
  const marginRight = resolveMarginRight(props);
  const marginTop = resolveMarginTop(props);
  const marginBottom = resolveMarginBottom(props);
  const paddingLeft = resolvePaddingLeft(props);
  const paddingRight = resolvePaddingRight(props);
  const paddingTop = resolvePaddingTop(props);
  const paddingBottom = resolvePaddingBottom(props);

  const vars = {
    '--view-bleed-left': bleedLeft && `calc(${getValue(bleedLeft)} * -1)`,
    '--view-bleed-right': bleedRight && `calc(${getValue(bleedRight)} * -1)`,
    '--view-bleed-top': bleedTop && `calc(${getValue(bleedTop)} * -1)`,
    '--view-bleed-bottom': bleedBottom && `calc(${getValue(bleedBottom)} * -1)`,

    '--view-margin-left':
      (bleedLeft || marginLeft) &&
      `var(--view-bleed-left, ${getValue(marginLeft) || ''})`,
    '--view-margin-right':
      (bleedRight || marginRight) &&
      `var(--view-bleed-right, ${getValue(marginRight) || ''})`,
    '--view-margin-top':
      (bleedTop || marginTop) &&
      `var(--view-bleed-top, ${getValue(marginTop) || ''})`,
    '--view-margin-bottom':
      (bleedBottom || marginBottom) &&
      `var(--view-bleed-bottom, ${getValue(marginBottom) || ''})`,

    '--view-padding-left': getValue(paddingLeft),
    '--view-padding-right': getValue(paddingRight),
    '--view-padding-top': getValue(paddingTop),
    '--view-padding-bottom': getValue(paddingBottom),

    '--view-col-span': getValue(props.gridColSpan),
    '--view-col-start': getValue(props.gridColStart),
    '--view-col-end': getValue(props.gridColEnd),
    '--view-row-span': getValue(props.gridRowSpan),
    '--view-row-start': getValue(props.gridRowStart),
    '--view-row-end': getValue(props.gridRowEnd),

    '--view-width': resolveDimension(props.width),
    '--view-height': resolveDimension(props.height),
    '--view-min-width': resolveDimension(props.minWidth),
    '--view-min-height': resolveDimension(props.minHeight),
    '--view-max-width': resolveDimension(props.maxWidth),
    '--view-max-height': resolveDimension(props.maxHeight),

    // special case: aspect ratio is a non-fractional value that includes a slash
    '--view-aspect-ratio': getValue(props.aspectRatio),
    '--view-flex-basis': resolveDimension(props.flexBasis),
  };
  const space = {
    bleedLeft,
    bleedRight,
    bleedTop,
    bleedBottom,
    marginLeft,
    marginRight,
    marginTop,
    marginBottom,
    paddingLeft,
    paddingRight,
    paddingTop,
    paddingBottom,
  };

  return { vars, space };
}

// Utils
// -----------------------------------------------------------------------------

function isDimensionFraction(value?: unknown): value is DimensionFraction {
  return typeof value === 'string' && value.includes('/');
}
function isDimensionContainer(value?: unknown): value is DimensionContainer {
  return (
    typeof value === 'string' &&
    Object.keys(DIMENSION_CONTAINER).includes(value)
  );
}

// prettier-ignore
export const selfLayoutStyleKeys = (['alignSelf', 'flex', 'flexBasis', 'flexGrow', 'flexShrink', 'gridColEnd', 'gridColSpan', 'gridColStart', 'gridRowEnd', 'gridRowSpan', 'gridRowStart', 'justifySelf', 'order', 'isHidden']) satisfies Array<keyof SelfLayoutProps>;
// prettier-ignore
export const bleedStyleKeys = (['bleed', 'bleedBottom', 'bleedLeft', 'bleedRight', 'bleedTop', 'bleedX', 'bleedY',]) satisfies Array<keyof BleedProps>;
// prettier-ignore
export const marginStyleKeys = (['margin', 'marginBottom', 'marginLeft', 'marginRight', 'marginTop', 'marginX', 'marginY']) satisfies Array<keyof MarginProps>;
// prettier-ignore
export const paddingStyleKeys = (['padding', 'paddingBottom', 'paddingLeft', 'paddingRight', 'paddingTop', 'paddingX', 'paddingY']) satisfies Array<keyof PaddingProps>;
// prettier-ignore
export const dimensionStyleKeys = (['aspectRatio', 'height', 'maxHeight', 'maxWidth', 'minHeight', 'minWidth', 'width']) satisfies Array<keyof DimensionProps>;

export const baseStyleKeys = [
  ...selfLayoutStyleKeys,
  ...marginStyleKeys,
] satisfies Array<keyof BaseStyleProps>;
export const boxStyleKeys = [
  ...dimensionStyleKeys,
  ...bleedStyleKeys,
  ...paddingStyleKeys,
] satisfies Array<keyof BoxStyleProps>;
export const allStyleKeys = [...baseStyleKeys, ...boxStyleKeys] satisfies Array<
  keyof StyleProps
>;

export function omitStyleProps<Props extends StyleProps>(props: Props) {
  return filterStyleProps(props, allStyleKeys).otherProps;
}

export function filterStyleProps<
  Props extends StyleProps,
  Keys extends keyof StyleProps,
>(props: Props, allowedKeys: Array<Keys>) {
  return {
    otherProps: omit(props, allowedKeys),
    styleProps: pick(props, allowedKeys),
  };
}

// Resolvers
// -----------------------------------------------------------------------------

function resolveBleedLeft(props: StyleProps) {
  return props.bleedLeft || props.bleedX || props.bleed;
}
function resolveBleedRight(props: StyleProps) {
  return props.bleedRight || props.bleedX || props.bleed;
}
function resolveBleedTop(props: StyleProps) {
  return props.bleedTop || props.bleedY || props.bleed;
}
function resolveBleedBottom(props: StyleProps) {
  return props.bleedBottom || props.bleedY || props.bleed;
}
function resolveMarginLeft(props: StyleProps) {
  return props.marginLeft || props.marginX || props.margin;
}
function resolveMarginRight(props: StyleProps) {
  return props.marginRight || props.marginX || props.margin;
}
function resolveMarginTop(props: StyleProps) {
  return props.marginTop || props.marginY || props.margin;
}
function resolveMarginBottom(props: StyleProps) {
  return props.marginBottom || props.marginY || props.margin;
}
function resolvePaddingLeft(props: StyleProps) {
  return props.paddingLeft || props.paddingX || props.padding;
}
function resolvePaddingRight(props: StyleProps) {
  return props.paddingRight || props.paddingX || props.padding;
}
function resolvePaddingTop(props: StyleProps) {
  return props.paddingTop || props.paddingY || props.padding;
}
function resolvePaddingBottom(props: StyleProps) {
  return props.paddingBottom || props.paddingY || props.padding;
}
